/**
 * Pack Feature Row
 *
 * One feature in the pack checklist: toggle, name, version, category, gate,
 * and (when in the pack) option and runtime-config controls.
 */

import type { ReactElement } from 'react';
import { OptionFieldRenderer } from '../OptionFieldRenderer';
import { RuntimeConfigButton } from './RuntimeConfigButton';
import { MissingRuntimeConfigBadge } from './MissingRuntimeConfigBanner';
import { FeatureVersion } from './FeatureVersion';
import { lineOptionSchema } from './lineOptionSchema';
import type { Feature, MissingRuntimeConfigItem } from '../../schemas/admin.schemas';

interface PackFeatureRowProps {
  feature: Feature;
  packId: string;
  packName?: string;
  inPack: boolean;
  isExpanded: boolean;
  toggleDisabled: boolean;
  requiredBy?: string[] | undefined;
  missingItem?: MissingRuntimeConfigItem | undefined;
  optionValues: Record<string, unknown>;
  onToggle: () => void;
  onExpand: () => void;
  onOptionChange: (key: string, value: unknown) => void;
  onSaveOptions: () => void;
}

export function PackFeatureRow({
  feature,
  packId,
  packName,
  inPack,
  isExpanded,
  toggleDisabled,
  requiredBy,
  missingItem,
  optionValues,
  onToggle,
  onExpand,
  onOptionChange,
  onSaveOptions,
}: PackFeatureRowProps): ReactElement {
  const lineValue = feature.variant_key ? optionValues[feature.variant_key] : undefined;
  const hasOptions = Object.keys(feature.option_schema).length > 0;
  return (
    <li className="project-features__item">
      <div className="project-features__row">
        <label className="project-features__cell-assigned">
          <input type="checkbox" checked={inPack} onChange={onToggle} disabled={toggleDisabled} />
        </label>
        <span className="project-features__cell-feature">
          {feature.display_name}
          {requiredBy && (
            <span className="feature-table__badge" style={{ marginLeft: '0.5rem' }}
              title={`Required by: ${requiredBy.join(', ')}`}>
              dep: {requiredBy.join(', ')}
            </span>
          )}
          {inPack && <MissingRuntimeConfigBadge item={missingItem} />}
        </span>
        <span className="project-features__cell-version feature-table__version">
          <FeatureVersion feature={feature} selectedLine={lineValue} />
        </span>
        <span className="project-features__cell-category">{feature.category}</span>
        <span className="project-features__cell-gate">
          {feature.is_gated && (
            <span className="feature-table__badge" title={`${feature.gate_attribute ?? ''} = ${feature.gate_value ?? ''}`}>
              {feature.gate_value ?? 'Gated'}
            </span>
          )}
        </span>
        <span className="project-features__cell-options">
          {inPack && hasOptions && (
            <button type="button" className="project-features__options-btn" onClick={onExpand}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} options for ${feature.display_name}`}>
              {isExpanded ? 'Close' : 'Options'}
            </button>
          )}
          {inPack && (
            <RuntimeConfigButton
              featureId={feature.id}
              featureName={feature.display_name}
              packId={packId}
              {...(packName ? { packName } : {})}
              className="project-features__options-btn"
              needsAttention={missingItem !== undefined}
            />
          )}
        </span>
      </div>
      {inPack && isExpanded && (
        <div className="project-features__options">
          <OptionFieldRenderer schema={lineOptionSchema(feature, lineValue)} values={optionValues}
            onChange={onOptionChange} showAdminBadge={true} />
          <button type="button" className="dialog__btn dialog__btn--primary" onClick={onSaveOptions}>
            Save Options
          </button>
        </div>
      )}
    </li>
  );
}
